import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  AccountBalance as AccountBalanceIcon,
  Close as CloseIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useUserStore } from "../../store";

import { can } from '../../utils/access';
// Per-provider configuration for the generic accounting-integration card.
// - staffOnly: the provider is not published yet (never round-tripped against a
//   live environment), so only staff see its card. The backend enforces the same
//   list in accounting_integrations/views.py STAFF_ONLY_PROVIDERS — keep them in
//   sync when publishing a provider. Netvisor published 2026-08-17, Fennoa
//   2026-08-21, Procountor 2026-11-04; nothing is gated right now.
// - credentialFields: name = key in the POST `credentials` object (topLevel fields
//   are sent at the payload root instead, e.g. Netvisor's organisation_business_id).
// - secret fields render as password inputs; when already configured, blank = keep.
// - testId overrides the default `${provider}-cred-${name}` (Netvisor keeps its
//   pre-refactor testids so the existing E2E spec stays valid).
export const PROVIDERS = {
  netvisor: {
    label: "Netvisor",
    description: {
      key: "netvisor.settings.description",
      default:
        "Siirrä laskut automaattisesti Netvisoriin heti, kun ne lähetetään asiakkaalle. Tarvitset Netvisorin rajapintatunnukset, jotka saat Netvisorin tuesta tai tilitoimistoltasi.",
    },
    credentialFields: [
      {
        name: "customer_id",
        label: { key: "netvisor.settings.customerId", default: "Käyttäjätunniste" },
        testId: "netvisor-customer-id",
      },
      {
        name: "customer_key",
        secret: true,
        label: { key: "netvisor.settings.customerKey", default: "API-avain" },
        testId: "netvisor-customer-key",
      },
      {
        name: "organisation_business_id",
        topLevel: true,
        label: { key: "netvisor.settings.businessId", default: "Y-tunnus Netvisorissa" },
        testId: "netvisor-business-id",
      },
    ],
    showAccountMapping: true,
    showPaymentMethods: true,
    showAttachmentToggle: true,
    showPrivateCustomerMode: true,
  },
  fennoa: {
    label: "Fennoa",
    description: { key: "fennoa.settings.description", default: "Siirrä myyntilaskut automaattisesti Fennoaan." },
    info: {
      key: "fennoa.settings.info",
      default: "Fennoa-tunnukset luo tilitoimistonne Fennoassa: Käyttäjät > Luo uusi API-käyttäjä.",
    },
    credentialFields: [
      { name: "username", label: { key: "fennoa.settings.username", default: "API-käyttäjätunnus" } },
      { name: "api_key", secret: true, label: { key: "fennoa.settings.apiKey", default: "API-avain" } },
    ],
    showAccountMapping: true,
    showPaymentMethods: false,
    // Fennoa keeps its own customer cards (customer_no) and takes the invoice
    // PDF as an attachment, so both settings do real work here.
    showAttachmentToggle: true,
    showPrivateCustomerMode: true,
  },
  procountor: {
    label: "Procountor",
    description: {
      key: "procountor.settings.description",
      default: "Siirrä myyntilaskut automaattisesti Procountoriin.",
    },
    info: {
      key: "procountor.settings.info",
      default:
        "Luo API-avain Procountorissa: Perustiedot > API-avaimet > Uusi API-avain. Kirjoita kenttään Asiakasohjelmiston tunniste tämä tunniste ja liitä syntynyt avain alle:",
    },
    credentialFields: [{ name: "api_key", secret: true, label: { key: "procountor.settings.apiKey", default: "API-avain" } }],
    // Each row's sales account is set on Procountor's ledger receipt before approval.
    showAccountMapping: true,
    showPaymentMethods: false,
    // The invoice PDF goes in as an attachment (POST /attachments) before approval.
    showAttachmentToggle: true,
  },
};

const EMPTY_METHOD = { name: "", account: "" };

const settingsFromData = (d) => ({
  send_mode: d?.send_mode || "verstaani_sends",
  cash_card_handling: d?.cash_card_handling || "exclude",
  transfer_start_date: d?.transfer_start_date || "",
  pull_payments: !!d?.pull_payments,
  attach_invoice_pdf: d?.attach_invoice_pdf !== false,
  private_customer_mode: d?.private_customer_mode || "collective",
  collective_customer_external_id: d?.collective_customer_external_id || "",
  sales_account_labor: d?.sales_account_labor || "",
  sales_account_parts: d?.sales_account_parts || "",
  sales_account_other: d?.sales_account_other || "",
  sales_account_margin: d?.sales_account_margin || "",
  sales_account_deductible: d?.sales_account_deductible || "",
  payment_methods: {
    cash: { ...EMPTY_METHOD, ...(d?.config?.payment_methods?.cash || {}) },
    card: { ...EMPTY_METHOD, ...(d?.config?.payment_methods?.card || {}) },
    financing: { ...EMPTY_METHOD, ...(d?.config?.payment_methods?.financing || {}) },
    refund_bank: { ...EMPTY_METHOD, ...(d?.config?.payment_methods?.refund_bank || {}) },
  },
});

// Free-text account field with the provider's chart of accounts as suggestions.
// The stored value is the bare account number; suggestions render "number name".
function AccountField({ label, value, onChange, accounts, testId, sx, helperText }) {
  return (
    <Autocomplete
      freeSolo
      options={accounts}
      getOptionLabel={(option) => (typeof option === "string" ? option : `${option.number} ${option.name}`)}
      value={value || ""}
      onChange={(event, newValue) => {
        onChange(typeof newValue === "string" ? newValue : newValue ? String(newValue.number) : "");
      }}
      onInputChange={(event, newInput, reason) => {
        if (reason === "input" || reason === "clear") {
          onChange(newInput);
        }
      }}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={label}
          helperText={helperText}
          inputProps={{ ...params.inputProps, "data-testid": testId }}
        />
      )}
    />
  );
}

// Strip the provider base URL so the table shows just the resource path.
const callResource = (url) => {
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}`;
  } catch (e) {
    return url || "";
  }
};

const PRE_SX = {
  whiteSpace: "pre-wrap",
  wordBreak: "break-all",
  maxHeight: 300,
  overflow: "auto",
  fontSize: 12,
  fontFamily: "monospace",
  bgcolor: "action.hover",
  borderRadius: 1,
  p: 1,
  m: 0,
};

// One log row + its expandable request/response detail row.
function ApiCallRow({ provider, call }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const resource = callResource(call.url);

  return (
    <>
      <TableRow
        hover
        onClick={() => setExpanded((prev) => !prev)}
        sx={{ cursor: "pointer", "& > td": { borderBottom: "none" } }}
        data-testid={`${provider}-api-call-row-${call.id}`}
      >
        <TableCell sx={{ width: "1%", p: 0.5 }}>
          <IconButton size="small">
            {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          {call.created_at ? new Date(call.created_at).toLocaleString() : "-"}
        </TableCell>
        <TableCell title={call.url}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "baseline",
              maxWidth: { xs: 160, sm: 280, md: 420 },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
              {call.method}
            </Typography>
            <Typography variant="body2" noWrap sx={{ fontFamily: "monospace", minWidth: 0 }}>
              {resource}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>{call.status_code ?? "-"}</TableCell>
        <TableCell>
          <Chip size="small" color={call.success ? "success" : "error"} label={call.success ? "OK" : "FAIL"} />
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          {call.duration_ms != null ? `${call.duration_ms} ms` : "-"}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
              {call.error && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-all" }}
                >
                  {call.error}
                </Typography>
              )}
              {call.request_body && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {t("accounting.apiCallRequest", "Pyyntö")}
                  </Typography>
                  <Box component="pre" sx={PRE_SX}>
                    {call.request_body}
                  </Box>
                </Box>
              )}
              {call.response_body && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {t("accounting.apiCallResponse", "Vastaus")}
                  </Typography>
                  <Box component="pre" sx={PRE_SX}>
                    {call.response_body}
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// Recent API calls to the provider (Smart debugging aid, staff-gated backend).
function ApiCallLogDialog({ provider, providerLabel, organizationId, open, onClose }) {
  const { t } = useTranslation();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCalls = useCallback(async () => {
    if (!organizationId || !open) return;
    setLoading(true);
    try {
      const resp = await axios.get(
        `/api/accounting-integrations/calls/?organization=${organizationId}&provider=${provider}&limit=100`
      );
      setCalls(resp.data.calls || []);
    } catch (e) {
      console.error("Failed to load accounting API calls", e);
      setCalls([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId, provider, open]);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { maxHeight: "85vh" } }}
      data-testid={`${provider}-api-calls-dialog`}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">
            {t("accounting.apiCallsTitle", {
              defaultValue: "API-kutsut – {{provider}}",
              provider: providerLabel,
            })}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="small"
              onClick={fetchCalls}
              disabled={loading}
              title={t("accounting.refresh", "Päivitä")}
              data-testid={`${provider}-api-calls-refresh`}
            >
              <RefreshIcon />
            </IconButton>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : calls.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            {t("accounting.noApiCalls", "Ei API-kutsuja vielä.")}
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: "65vh" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "1%" }} />
                  <TableCell>{t("accounting.apiCallTime", "Aika")}</TableCell>
                  <TableCell>{t("accounting.apiCallResource", "Resurssi")}</TableCell>
                  <TableCell>HTTP</TableCell>
                  <TableCell>{t("accounting.apiCallStatus", "Tila")}</TableCell>
                  <TableCell>{t("accounting.apiCallDuration", "Kesto")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {calls.map((call) => (
                  <ApiCallRow key={call.id} provider={provider} call={call} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Close")}</Button>
      </DialogActions>
    </Dialog>
  );
}

// Generic accounting-provider integration card (Netvisor, Fennoa, Procountor).
// `activeProvider` is the org's currently active provider (or null); when it
// differs from this card's provider, activation asks for confirmation because
// the backend deactivates the other provider on POST.
export default function ProviderIntegrationCard({ provider, activeProvider, refreshToken, onStatusChange }) {
  const config = PROVIDERS[provider];
  const { t } = useTranslation();
  const { currentOrganization, fetchUser } = useUserStore();
  const organizationId = currentOrganization?.organization_id;
  const isAdmin = can(currentOrganization, "settings.integrations");

  const emptyForm = useMemo(
    () => Object.fromEntries(config.credentialFields.map((f) => [f.name, ""])),
    [config]
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [showSecret, setShowSecret] = useState({});
  const [error, setError] = useState(null); // { message, providerError }
  const [testResult, setTestResult] = useState(null);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [switchConfirmOpen, setSwitchConfirmOpen] = useState(false);
  const [apiCallsOpen, setApiCallsOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Settings form (visible when configured and active)
  const [settings, setSettings] = useState(settingsFromData(null));
  const [savingSettings, setSavingSettings] = useState(false);

  // Chart of accounts for the Autocomplete suggestions
  const [accounts, setAccounts] = useState([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [accountsError, setAccountsError] = useState(false);

  const providerLabel = config.label;
  const needsAccounts = config.showAccountMapping || config.showPaymentMethods;

  const fetchStatus = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const resp = await axios.get(
        `/api/accounting-integrations/integration/?organization=${organizationId}&provider=${provider}`
      );
      setData(resp.data);
      setSettings(settingsFromData(resp.data));
    } catch (e) {
      console.error("Failed to load accounting integration", e);
    } finally {
      setLoading(false);
    }
  }, [organizationId, provider]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus, refreshToken]);

  // Load the chart of accounts once when the settings section becomes visible.
  useEffect(() => {
    if (!data?.configured || !data?.is_active || !needsAccounts || accountsLoaded || !organizationId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(
          `/api/accounting-integrations/accounts/?organization=${organizationId}&provider=${provider}`
        );
        if (!cancelled) setAccounts(resp.data.accounts || []);
      } catch (e) {
        // 502 = provider unreachable; the account fields stay usable as plain free text.
        if (!cancelled) {
          setAccounts([]);
          setAccountsError(true);
        }
      } finally {
        if (!cancelled) setAccountsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data?.configured, data?.is_active, needsAccounts, accountsLoaded, organizationId, provider]);

  const credentialValue = (fieldName) => data?.credentials?.[fieldName] ?? data?.[fieldName] ?? "";

  const startEdit = () => {
    const prefilled = { ...emptyForm };
    config.credentialFields.forEach((f) => {
      if (!f.secret) prefilled[f.name] = credentialValue(f.name);
    });
    setForm(prefilled);
    setError(null);
    setTestResult(null);
    setEditing(true);
  };

  const doSaveCredentials = async () => {
    setSwitchConfirmOpen(false);
    setBusy(true);
    setError(null);
    setTestResult(null);
    try {
      const credentials = {};
      const payload = { provider, credentials };
      config.credentialFields.forEach((f) => {
        const raw = form[f.name] || "";
        const value = f.secret ? raw : raw.trim();
        // When already configured, an empty secret means "keep the current one".
        if (f.secret && data?.configured && !value) return;
        if (f.topLevel) {
          payload[f.name] = value;
        } else {
          credentials[f.name] = value;
        }
      });
      await axios.post(`/api/accounting-integrations/integration/?organization=${organizationId}`, payload);
      setEditing(false);
      setForm(emptyForm);
      await fetchStatus();
      await fetchUser();
      if (onStatusChange) onStatusChange();
    } catch (err) {
      setError({
        message: err.response?.data?.error || t("accounting.saveError", "Tallennus epäonnistui."),
        providerError: err.response?.data?.provider_error || "",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleSaveCredentials = () => {
    // Activating this provider deactivates the currently active one server-side.
    if (activeProvider && activeProvider !== provider) {
      setSwitchConfirmOpen(true);
      return;
    }
    doSaveCredentials();
  };

  const handleTest = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      await axios.post(`/api/accounting-integrations/integration/test/?organization=${organizationId}`, { provider });
      setTestResult({ success: true });
    } catch (err) {
      setTestResult({
        success: false,
        error: err.response?.data?.error || t("accounting.testError", "Yhteystesti epäonnistui."),
        providerError: err.response?.data?.provider_error || "",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    setConfirmRemoveOpen(false);
    try {
      await axios.delete(
        `/api/accounting-integrations/integration/?organization=${organizationId}&provider=${provider}`
      );
      setEditing(false);
      setForm(emptyForm);
      setTestResult(null);
      await fetchStatus();
      await fetchUser();
      if (onStatusChange) onStatusChange();
    } catch (err) {
      setError({
        message: err.response?.data?.error || t("accounting.saveError", "Tallennus epäonnistui."),
        providerError: "",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {
        provider,
        send_mode: settings.send_mode,
        cash_card_handling: settings.cash_card_handling,
        transfer_start_date: settings.transfer_start_date || null,
        pull_payments: settings.pull_payments,
        attach_invoice_pdf: settings.attach_invoice_pdf,
        private_customer_mode: settings.private_customer_mode,
        collective_customer_external_id: settings.collective_customer_external_id,
        sales_account_labor: settings.sales_account_labor,
        sales_account_parts: settings.sales_account_parts,
        sales_account_other: settings.sales_account_other,
        sales_account_margin: settings.sales_account_margin,
        sales_account_deductible: settings.sales_account_deductible,
        config: { ...(data?.config || {}), payment_methods: settings.payment_methods },
      };
      const resp = await axios.patch(
        `/api/accounting-integrations/integration/?organization=${organizationId}`,
        payload
      );
      setData(resp.data);
      setSettings(settingsFromData(resp.data));
      setSnackbar({
        open: true,
        message: t("accounting.settingsSaved", "Asetukset tallennettu"),
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t("accounting.saveError", "Tallennus epäonnistui."),
        severity: "error",
      });
    } finally {
      setSavingSettings(false);
    }
  };

  const setSetting = (field) => (value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const setPaymentMethod = (methodKey, field) => (value) => {
    setSettings((prev) => ({
      ...prev,
      payment_methods: {
        ...prev.payment_methods,
        [methodKey]: { ...prev.payment_methods[methodKey], [field]: value },
      },
    }));
  };

  const canSave =
    !busy &&
    config.credentialFields.every((f) =>
      f.secret ? data?.configured || form[f.name] : (form[f.name] || "").trim()
    );

  const methodLabels = {
    cash: t("accounting.cash", "Käteinen"),
    card: t("accounting.card", "Kortti"),
    financing: t("accounting.financing", "Rahoitus"),
    refund_bank: t("accounting.refundBank", "Palautus pankkitililtä"),
  };

  const infoText = config.info ? t(config.info.key, config.info.default) : "";
  // Netvisor: the customer must allow our integration in their own Netvisor
  // using this key before any call works. Server-provided (env), not a secret.
  const permissionKey = data?.provider_info?.permission_key || "";
  // Procountor: our API client id, which the customer types into Procountor's
  // "New API key" dialog. Server-provided (env), not a secret.
  const clientId = data?.provider_info?.client_id || "";
  // A POS payment cannot be pushed without the accounting system's own name for
  // the payment method, so surface the gap before it fails on a real invoice.
  const missingMethodNames =
    settings.cash_card_handling === "paid"
      ? ["cash", "card", "financing", "refund_bank"].filter(
          (key) => settings.payment_methods[key].account && !settings.payment_methods[key].name
        )
      : [];

  return (
    <Card variant="outlined" sx={{ gridColumn: { md: "1 / -1" } }} data-testid={`${provider}-card`}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
          >
            <AccountBalanceIcon />
          </Box>
          {data?.configured && (
            <Chip
              size="small"
              color={data.is_active ? "success" : "default"}
              label={
                data.is_active
                  ? t("accounting.active", "Käytössä")
                  : t("accounting.inactive", "Ei käytössä")
              }
              sx={{ ml: "auto" }}
              data-testid={`${provider}-status-chip`}
            />
          )}
        </Box>

        <Typography variant="h6" gutterBottom>
          {t(`${provider}.settings.title`, providerLabel)}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(config.description.key, config.description.default)}
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} data-testid={`${provider}-error`}>
                {error.message}
                {error.providerError && (
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{ mt: 0.5, fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                  >
                    {error.providerError}
                  </Typography>
                )}
              </Alert>
            )}
            {testResult && (
              <Alert
                severity={testResult.success ? "success" : "error"}
                sx={{ mb: 2 }}
                data-testid={`${provider}-test-result`}
              >
                {testResult.success ? t("accounting.testSuccess", "Yhteys toimii.") : testResult.error}
                {!testResult.success && testResult.providerError && (
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{ mt: 0.5, fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                  >
                    {testResult.providerError}
                  </Typography>
                )}
              </Alert>
            )}

            {!editing && data?.configured && (
              <>
                <Box sx={{ mb: 2 }}>
                  {config.credentialFields
                    .filter((f) => !f.secret)
                    .map((f) => (
                      <Typography key={f.name} variant="body2" sx={{ mb: 0.5 }}>
                        {t(f.label.key, f.label.default)}: <code>{credentialValue(f.name)}</code>
                      </Typography>
                    ))}
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleTest}
                    disabled={busy}
                    data-testid={`${provider}-test`}
                  >
                    {busy ? <CircularProgress size={16} /> : t("accounting.testConnection", "Testaa yhteys")}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setApiCallsOpen(true)}
                    data-testid={`${provider}-api-calls`}
                  >
                    {t("accounting.apiCalls", "API-kutsut")}
                  </Button>
                  {isAdmin && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={startEdit}
                      disabled={busy}
                      data-testid={`${provider}-edit`}
                    >
                      {t("accounting.editCredentials", "Muokkaa tunnuksia")}
                    </Button>
                  )}
                  {isAdmin && data.is_active && (
                    <Button
                      variant="text"
                      color="error"
                      size="small"
                      onClick={() => setConfirmRemoveOpen(true)}
                      disabled={busy}
                      data-testid={`${provider}-deactivate`}
                    >
                      {t("accounting.deactivate", "Poista käytöstä")}
                    </Button>
                  )}
                  {isAdmin && !data.is_active && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={startEdit}
                      disabled={busy}
                      data-testid={`${provider}-activate`}
                    >
                      {t("accounting.activate", "Ota käyttöön")}
                    </Button>
                  )}
                </Box>
              </>
            )}

            {!editing && !data?.configured && isAdmin && (
              <Button variant="contained" size="small" onClick={startEdit} data-testid={`${provider}-activate`}>
                {t("accounting.activate", "Ota käyttöön")}
              </Button>
            )}

            {editing && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 480 }}>
                {infoText && (
                  <Alert severity="info">
                    {infoText}
                    {clientId && (
                      <Box
                        component="code"
                        data-testid={`${provider}-client-id`}
                        sx={{
                          display: "block",
                          mt: 1,
                          p: 1,
                          borderRadius: 1,
                          bgcolor: "action.hover",
                          fontFamily: "monospace",
                          fontSize: "13px",
                          wordBreak: "break-all",
                        }}
                      >
                        {clientId}
                      </Box>
                    )}
                  </Alert>
                )}
                {permissionKey && (
                  <Alert severity="warning" data-testid={`${provider}-permission-key`}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {t(
                        "accounting.permissionKeyIntro",
                        "Avaa ensin rajapintaliikenne Netvisorissa: Yritysvalikko > Rajapintaresurssien käyttöoikeuksien salliminen. Anna siellä tämä käyttöoikeusavain:"
                      )}
                    </Typography>
                    <Box
                      component="code"
                      sx={{
                        display: "block",
                        p: 1,
                        borderRadius: 1,
                        bgcolor: "action.hover",
                        fontFamily: "monospace",
                        fontSize: "13px",
                        wordBreak: "break-all",
                      }}
                    >
                      {permissionKey}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                      {t(
                        "accounting.permissionKeyHint",
                        "Ilman tätä yhteys ei toimi, vaikka tunnukset olisivat oikein."
                      )}
                    </Typography>
                  </Alert>
                )}
                {config.credentialFields.map((f) => (
                  <TextField
                    key={f.name}
                    fullWidth
                    size="small"
                    type={f.secret && !showSecret[f.name] ? "password" : "text"}
                    label={t(f.label.key, f.label.default)}
                    helperText={
                      f.secret && data?.configured
                        ? t("accounting.credentialKeepHelp", "Jätä tyhjäksi säilyttääksesi nykyinen avain")
                        : ""
                    }
                    value={form[f.name]}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    inputProps={{ "data-testid": f.testId || `${provider}-cred-${f.name}` }}
                    InputProps={
                      f.secret
                        ? {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setShowSecret((prev) => ({ ...prev, [f.name]: !prev[f.name] }))
                                  }
                                >
                                  {showSecret[f.name] ? (
                                    <VisibilityOffIcon fontSize="small" />
                                  ) : (
                                    <VisibilityIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }
                        : undefined
                    }
                  />
                ))}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveCredentials}
                    disabled={!canSave}
                    data-testid={`${provider}-save`}
                  >
                    {busy ? <CircularProgress size={16} /> : t("Save")}
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setEditing(false)}
                    disabled={busy}
                    data-testid={`${provider}-cancel`}
                  >
                    {t("Cancel")}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Transfer settings — own save button, PATCHes the integration (foreman+) */}
            {data?.configured && data?.is_active && (
              <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  {t("accounting.settingsTitle", "Asetukset")}
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, maxWidth: 640 }}>
                  <FormControl>
                    <FormLabel sx={{ fontSize: "14px" }}>{t("accounting.sendMode", "Laskujen lähetys")}</FormLabel>
                    <RadioGroup
                      value={settings.send_mode}
                      onChange={(e) => setSetting("send_mode")(e.target.value)}
                    >
                      <FormControlLabel
                        value="verstaani_sends"
                        control={
                          <Radio size="small" inputProps={{ "data-testid": `${provider}-send-mode-verstaani` }} />
                        }
                        label={t("accounting.sendModeVerstaani", "Verstaani lähettää laskut asiakkaille")}
                      />
                      <FormControlLabel
                        value="provider_sends"
                        disabled
                        control={<Radio size="small" />}
                        label={`${t("accounting.sendModeProvider", {
                          defaultValue: "{{provider}} lähettää laskut asiakkaille",
                          provider: providerLabel,
                        })} (${t("accounting.sendModeComingSoon", "tulossa")})`}
                      />
                    </RadioGroup>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>{t("accounting.cashCardHandling", "Käteis-, kortti- ja rahoitusmyynti")}</InputLabel>
                    <Select
                      value={settings.cash_card_handling}
                      onChange={(e) => setSetting("cash_card_handling")(e.target.value)}
                      label={t("accounting.cashCardHandling", "Käteis-, kortti- ja rahoitusmyynti")}
                      inputProps={{ "data-testid": `${provider}-cash-card-handling` }}
                    >
                      <MenuItem value="exclude">{t("accounting.cashCardExclude", "Ei siirretä (kassaraportti)")}</MenuItem>
                      <MenuItem value="paid">{t("accounting.cashCardPaid", "Siirretään ja merkitään maksetuksi")}</MenuItem>
                      <MenuItem value="open">{t("accounting.cashCardOpen", "Siirretään avoimena")}</MenuItem>
                    </Select>
                  </FormControl>

                  {config.showPaymentMethods && settings.cash_card_handling === "paid" && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {t("accounting.paymentMethods", "Maksutavat kirjanpidossa")}
                      </Typography>
                      {missingMethodNames.length > 0 && (
                        <Alert severity="warning" data-testid={`${provider}-method-name-warning`}>
                          {t(
                            "accounting.methodNameMissing",
                            "Maksutavan nimen on vastattava kirjanpidossa olevaa maksutapaa. Ilman nimeä suoritusta ei voi siirtää: {{methods}}",
                            { methods: missingMethodNames.join(", ") }
                          )}
                        </Alert>
                      )}
                      {["cash", "card", "financing", "refund_bank"].map((methodKey) => (
                        <Box key={methodKey} sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                          <Typography variant="body2" sx={{ width: 72, flexShrink: 0 }}>
                            {methodLabels[methodKey]}
                          </Typography>
                          <TextField
                            size="small"
                            label={t("accounting.methodName", "Maksutavan nimi kirjanpidossa")}
                            value={settings.payment_methods[methodKey].name}
                            onChange={(e) => setPaymentMethod(methodKey, "name")(e.target.value)}
                            sx={{ flex: 1, minWidth: 180 }}
                            inputProps={{ "data-testid": `${provider}-method-name-${methodKey}` }}
                          />
                          <AccountField
                            label={t("accounting.methodAccount", "Maksutavan tili")}
                            value={settings.payment_methods[methodKey].account}
                            onChange={setPaymentMethod(methodKey, "account")}
                            accounts={accounts}
                            testId={`${provider}-method-account-${methodKey}`}
                            sx={{ flex: 1, minWidth: 180 }}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}

                  <TextField
                    size="small"
                    type="date"
                    label={t("accounting.startDate", "Siirron aloituspäivä")}
                    helperText={t("accounting.startDateHelp", "Vain tästä päivästä alkaen luodut laskut siirretään.")}
                    value={settings.transfer_start_date}
                    onChange={(e) => setSetting("transfer_start_date")(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ "data-testid": `${provider}-start-date` }}
                    sx={{ maxWidth: 260 }}
                  />

                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.pull_payments}
                          onChange={(e) => setSetting("pull_payments")(e.target.checked)}
                          inputProps={{ "data-testid": `${provider}-pull-payments` }}
                        />
                      }
                      label={t("accounting.pullPayments", "Hae maksutiedot kirjanpidosta")}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
                      {t("accounting.pullPaymentsHelp", "Laskut merkitään maksetuiksi, kun suoritus näkyy kirjanpidossa.")}
                    </Typography>
                  </Box>

                  {config.showAttachmentToggle && (
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.attach_invoice_pdf}
                            onChange={(e) => setSetting("attach_invoice_pdf")(e.target.checked)}
                            inputProps={{ "data-testid": `${provider}-attach-pdf` }}
                          />
                        }
                        label={t("accounting.attachPdf", "Liitä laskun PDF")}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
                        {t("accounting.attachPdfHelp", "Laskun kuva siirtyy kirjanpitoon laskun liitteeksi.")}
                      </Typography>
                    </Box>
                  )}

                  {config.showPrivateCustomerMode && (
                  <FormControl fullWidth size="small">
                    <InputLabel>{t("accounting.privateCustomerMode", "Yksityisasiakkaiden käsittely")}</InputLabel>
                    <Select
                      value={settings.private_customer_mode}
                      onChange={(e) => setSetting("private_customer_mode")(e.target.value)}
                      label={t("accounting.privateCustomerMode", "Yksityisasiakkaiden käsittely")}
                      inputProps={{ "data-testid": `${provider}-private-customer-mode` }}
                    >
                      <MenuItem value="collective">{t("accounting.collective", "Yksi kokoojaasiakas")}</MenuItem>
                      <MenuItem value="individual">{t("accounting.individual", "Jokainen asiakas erikseen")}</MenuItem>
                    </Select>
                  </FormControl>
                  )}

                  {config.showPrivateCustomerMode && settings.private_customer_mode === "collective" && (
                    <TextField
                      size="small"
                      label={t("accounting.collectiveCode", "Kokoojaasiakkaan asiakastunnus kirjanpidossa")}
                      value={settings.collective_customer_external_id}
                      onChange={(e) => setSetting("collective_customer_external_id")(e.target.value)}
                      inputProps={{ "data-testid": `${provider}-collective-code` }}
                    />
                  )}

                  {config.showAccountMapping ? (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5 }}>
                        {t("accounting.salesAccounts", "Myyntitilit")}
                      </Typography>
                      {accountsLoaded && accountsError && (
                        <Alert severity="info" sx={{ mb: 1.5 }}>
                          {t("accounting.accountsLoadError", "Tilikarttaa ei voitu hakea. Voit syöttää tilinumerot käsin.")}
                        </Alert>
                      )}
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                        <AccountField
                          label={t("accounting.accountLabor", "Työn myyntitili")}
                          value={settings.sales_account_labor}
                          onChange={setSetting("sales_account_labor")}
                          accounts={accounts}
                          testId={`${provider}-account-labor`}
                        />
                        <AccountField
                          label={t("accounting.accountParts", "Varaosien myyntitili")}
                          value={settings.sales_account_parts}
                          onChange={setSetting("sales_account_parts")}
                          accounts={accounts}
                          testId={`${provider}-account-parts`}
                        />
                        <AccountField
                          label={t("accounting.accountOther", "Muun myynnin myyntitili")}
                          value={settings.sales_account_other}
                          onChange={setSetting("sales_account_other")}
                          accounts={accounts}
                          testId={`${provider}-account-other`}
                        />
                        <AccountField
                          label={t("accounting.accountMargin", "Marginaaliverollisten myyntitili")}
                          value={settings.sales_account_margin}
                          onChange={setSetting("sales_account_margin")}
                          accounts={accounts}
                          testId={`${provider}-account-margin`}
                        />
                        <AccountField
                          label={t("accounting.accountDeductible", "Omavastuiden tili")}
                          value={settings.sales_account_deductible}
                          onChange={setSetting("sales_account_deductible")}
                          accounts={accounts}
                          testId={`${provider}-account-deductible`}
                          helperText={t(
                            "accounting.accountDeductibleHelp",
                            "Vakuutus- ja leasinglaskujen omavastuurivit. Tyhjänä käytetään muun myynnin tiliä."
                          )}
                        />
                      </Box>
                    </Box>
                  ) : (
                    config.accountMappingNote && (
                      <Alert severity="info">{t(config.accountMappingNote.key, config.accountMappingNote.default)}</Alert>
                    )
                  )}

                  <Box>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSaveSettings}
                      disabled={savingSettings}
                      data-testid={`${provider}-settings-save`}
                    >
                      {savingSettings ? <CircularProgress size={16} /> : t("accounting.saveSettings", "Tallenna asetukset")}
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}

        <Dialog open={confirmRemoveOpen} onClose={() => setConfirmRemoveOpen(false)}>
          <DialogTitle>
            {t("accounting.deactivateConfirmTitle", {
              defaultValue: "Poistetaanko {{provider}}-integraatio käytöstä?",
              provider: providerLabel,
            })}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t(
                "accounting.deactivateConfirmText",
                "Laskuja ei enää siirretä kirjanpitoon. Tallennetut tunnukset säilyvät ja voit ottaa integraation myöhemmin uudelleen käyttöön."
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmRemoveOpen(false)}>{t("Cancel")}</Button>
            <Button color="error" onClick={handleRemove} data-testid={`${provider}-deactivate-confirm`}>
              {t("accounting.deactivate", "Poista käytöstä")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={switchConfirmOpen} onClose={() => setSwitchConfirmOpen(false)}>
          <DialogTitle>{t("accounting.switchProviderTitle", "Vaihdetaanko kirjanpitojärjestelmä?")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t(
                "accounting.switchProviderConfirm",
                "Toisen kirjanpitojärjestelmän siirto on käytössä. Käyttöönotto poistaa sen käytöstä. Jatketaanko?"
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSwitchConfirmOpen(false)}>{t("Cancel")}</Button>
            <Button
              variant="contained"
              onClick={doSaveCredentials}
              data-testid={`${provider}-switch-confirm`}
            >
              {t("accounting.activate", "Ota käyttöön")}
            </Button>
          </DialogActions>
        </Dialog>

        <ApiCallLogDialog
          provider={provider}
          providerLabel={providerLabel}
          organizationId={organizationId}
          open={apiCallsOpen}
          onClose={() => setApiCallsOpen(false)}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
}
